import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Play, Pause, ChevronLeft, ChevronRight, Maximize2, Minimize2, Timer, MapPin, Calendar, User } from 'lucide-react';
import PhotoFrame from './PhotoFrame';
import { fmtTaken } from './PhotoInspectModal';

const SPEEDS = [3, 5, 8, 12];

// Auto-playing full-screen slideshow with captions for any list of photos (album, event group, gallery).
export default function PhotoSlideshow({ photos, title, startIndex = 0, onClose, mediaUrl, intervalSec = 5 }) {
  const [idx, setIdx] = useState(startIndex);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(intervalSec);
  const [showUi, setShowUi] = useState(true);
  const [fs, setFs] = useState(false);
  const rootRef = useRef(null);
  const hideTimer = useRef(null);
  const n = photos.length;

  const next = useCallback(() => setIdx((i) => (i + 1) % n), [n]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + n) % n), [n]);

  useEffect(() => {
    if (!playing || n < 2) return undefined;
    const t = setInterval(next, speed * 1000);
    return () => clearInterval(t);
  }, [playing, speed, next, n]);

  useEffect(() => {
    const k = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === ' ') { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [onClose, next, prev]);

  useEffect(() => {
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs); return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const poke = () => {
    setShowUi(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowUi(false), 2500);
  };
  useEffect(() => { poke(); return () => clearTimeout(hideTimer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFs = async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); else if (rootRef.current) await rootRef.current.requestFullscreen(); } catch { /* unsupported */ }
  };

  const p = photos[idx];
  if (!p) return null;
  const ui = `transition-opacity duration-500 ${showUi ? 'opacity-100' : 'opacity-0'}`;
  return (
    <div ref={rootRef} className="fixed inset-0 z-[150] flex flex-col" style={{ background: '#000', cursor: showUi ? 'default' : 'none' }} onMouseMove={poke} onClick={poke} data-testid="photo-slideshow">
      <div className={`absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-3 ${ui}`} style={{ background: 'linear-gradient(rgba(0,0,0,0.7), transparent)' }}>
        <div className="text-white min-w-0">
          <div className="font-title text-sm truncate" data-testid="slideshow-title">{title}</div>
          <div className="text-[11px] opacity-80" data-testid="slideshow-counter">{idx + 1} / {n}</div>
        </div>
        <div className="flex items-center gap-1.5 text-white">
          <span className="text-[11px] opacity-80 inline-flex items-center gap-1 mr-1"><Timer size={12} /> every</span>
          {SPEEDS.map((s) => <button key={s} onClick={(e) => { e.stopPropagation(); setSpeed(s); }} data-testid={`slideshow-speed-${s}`} className="px-1.5 py-0.5 rounded text-[11px]" style={{ background: speed === s ? 'var(--gold)' : 'rgba(255,255,255,0.15)', color: speed === s ? '#000' : '#fff' }}>{s}s</button>)}
          <button onClick={(e) => { e.stopPropagation(); toggleFs(); }} className="p-1.5 ml-2" title={fs ? 'Exit full screen' : 'Full screen'} data-testid="slideshow-fullscreen">{fs ? <Minimize2 size={18} /> : <Maximize2 size={18} />}</button>
          <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="p-1.5" title="Close" data-testid="slideshow-close"><X size={22} /></button>
        </div>
      </div>

      <div className="flex-1 min-h-0 relative">
        <PhotoFrame key={p.id} src={mediaUrl(p.url)} display={p.display} alt={p.title} className="w-full h-full fade-in" testId="slideshow-image" />
        {n > 1 && <button onClick={(e) => { e.stopPropagation(); prev(); }} className={`absolute left-3 top-1/2 -translate-y-1/2 text-white p-2 rounded-full ${ui}`} style={{ background: 'rgba(0,0,0,0.4)' }} data-testid="slideshow-prev"><ChevronLeft size={30} /></button>}
        {n > 1 && <button onClick={(e) => { e.stopPropagation(); next(); }} className={`absolute right-3 top-1/2 -translate-y-1/2 text-white p-2 rounded-full ${ui}`} style={{ background: 'rgba(0,0,0,0.4)' }} data-testid="slideshow-next"><ChevronRight size={30} /></button>}
      </div>

      <div className="absolute bottom-0 inset-x-0 z-10 px-5 pb-5 pt-10 text-white" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' }}>
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0" data-testid="slideshow-caption">
            {p.title && <h3 className="font-title text-xl md:text-2xl truncate">{p.title}</h3>}
            {p.description && <p className="text-sm opacity-90 mt-1 line-clamp-2">{p.description}</p>}
            <div className="flex flex-wrap gap-3 text-xs opacity-80 mt-1.5">
              {p.member_name && <span className="inline-flex items-center gap-1"><User size={11} /> {p.member_name}</span>}
              {(p.taken_at || p.date_taken) && <span className="inline-flex items-center gap-1"><Calendar size={11} /> {fmtTaken(p)}</span>}
              {p.place && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.place}</span>}
            </div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); setPlaying((v) => !v); }} className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-title text-sm" style={{ background: 'var(--gold)', color: '#000' }} data-testid="slideshow-playpause">
            {playing ? <><Pause size={15} /> Pause</> : <><Play size={15} /> Play</>}
          </button>
        </div>
        {n > 1 && (
          <div className="flex gap-1 mt-3" data-testid="slideshow-progress">
            {photos.slice(0, 60).map((x, i) => <button key={x.id} onClick={(e) => { e.stopPropagation(); setIdx(i); }} className="h-1 flex-1 rounded-full" style={{ background: i === idx ? 'var(--gold)' : i < idx ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.25)' }} aria-label={`Go to photo ${i + 1}`} />)}
          </div>
        )}
      </div>
    </div>
  );
}
